import { Box, Chip, useTheme } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "../../theme/statusChip.js";

export type StatusKey =
  | "open"
  | "in_progress"
  | "needs_review"
  | "closed"
  | "archived";

export interface StatusPillProps {
  status: StatusKey;
  label?: string;
  dense?: boolean;
}

const LABELS: Record<StatusKey, string> = {
  open: "Open",
  in_progress: "In progress",
  needs_review: "Needs review",
  closed: "Closed",
  archived: "Archived",
};

/**
 * Each key maps to one of D81's five slots, which are named by MEANING so a
 * surface never invents a status colour at the call site.
 *
 * `archived` used to have a bucket of its own — a lower-alpha grey. It does not
 * need one: archived is inert, which is what `neutral` means, and the label
 * already says "Archived". A sixth tint that differs from the fifth only in
 * opacity is a distinction the eye cannot make and the system does not have.
 */
const STATUS_TO_SLOT: Record<StatusKey, StatusSlot> = {
  open: "neutral", // inert · not started
  in_progress: "info", // informational · running
  needs_review: "attention", // needs a human
  closed: "success", // completed cleanly
  archived: "neutral",
};

/**
 * D91's chip recipe: the hue owns the fill, the dot and the LABEL (lifted only
 * as far as AA on the fill requires); there is no keyline.
 *
 * The dot is not decoration. It is what makes D26 structural rather than
 * advisory — a tinted fill is exactly the signal that disappears for a
 * colour-blind reader or on a poor display, and the label's hue goes with it.
 * Dot and text remain: two signals, one of which is words.
 */
export function StatusPill({ status, label, dense = false }: StatusPillProps) {
  const theme = useTheme();
  const style = statusChipStyle(theme, STATUS_TO_SLOT[status]);
  const text = label ?? LABELS[status];

  return (
    <Chip
      label={text}
      size={dense ? "small" : "medium"}
      variant="filled"
      icon={
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: "999px",
            backgroundColor: style.dotColor,
            flexShrink: 0,
          }}
        />
      }
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        // `flexShrink: 0` prevents the chip collapsing inside a narrow flex
        // parent (FindingsPanel rows in the freeform List widget) — an axe
        // contrast false-positive fires only when its intrinsic width hits 0.
        flexShrink: 0,
        fontWeight: 500,
        "& .MuiChip-label": { color: style.color },
        "& .MuiChip-icon": { marginInlineStart: "8px", marginInlineEnd: 0 },
      }}
      data-status={status}
      aria-label={`Status: ${LABELS[status]}`}
    />
  );
}
